import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import { CategoriesManager } from '../../hooks/useCategoriesManager';
import { CategoryType } from '../../types';
import { uploadsService } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';
import RichTextEditor from '../RichTextEditor';
import { CATEGORY_COVER_HINT_FA } from '../../constants/frontendImageHints';

type Props = {
  manager: CategoriesManager;
};

const suggestSlugFromName = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u0600-\u06FF-]/gi, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/** برای پیش‌نمایش سئو و لیست‌ها — بدون اجرای HTML */
const plainTextFromHtml = (html: string) => {
  if (!html || !html.includes('<')) return html.trim();
  if (typeof document !== 'undefined') {
    const d = document.createElement('div');
    d.innerHTML = html;
    return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
  }
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const CategoryFormDialogs: React.FC<Props> = ({ manager }) => {
  const {
    isModalOpen,
    isSaving,
    editing,
    form,
    setForm,
    moveTarget,
    moveParentId,
    setMoveParentId,
    setMoveTarget,
    parentOptions,
    moveOptions,
    resetAndClose,
    handleSubmit,
    handleMove,
    setError,
  } = manager;

  const [formTab, setFormTab] = useState(0);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isModalOpen) setFormTab(0);
  }, [isModalOpen]);

  const seoTitlePreview = form.metaTitle || form.name || 'عنوان دسته‌بندی';
  const seoDescriptionSource = form.metaDescription?.trim() || plainTextFromHtml(form.description || '');
  const seoDescriptionPreview =
    seoDescriptionSource.length > 200
      ? `${seoDescriptionSource.slice(0, 200)}…`
      : seoDescriptionSource || 'توضیح متا برای دسته‌بندی';
  const coverPreviewUrl = getImageUrl(form.image || undefined);
  const ogPreviewUrl = getImageUrl(
    form.syncCoverToSocial && form.image ? form.image : form.ogImage || undefined,
  );

  const onCoverFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      setImageUploading(true);
      setError('');
      try {
        const res = await uploadsService.uploadImage(file);
        const key = res.original || res.processed || res.thumbnail;
        if (!key || typeof key !== 'string') {
          throw new Error('آدرس فایل از سرور دریافت نشد');
        }
        setForm((p) => ({ ...p, image: key }));
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'آپلود تصویر ناموفق بود';
        setError(msg);
      } finally {
        setImageUploading(false);
      }
    },
    [setError, setForm],
  );

  return (
    <>
      <Dialog
        open={isModalOpen}
        onClose={imageUploading ? undefined : resetAndClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'rgba(15, 23, 42, 0.08)',
              boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.18)',
              maxHeight: 'calc(100dvh - 24px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: '1 1 auto',
            overflow: 'hidden',
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              pt: 2.5,
              px: 3,
              flexShrink: 0,
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 800, color: '#0F172A' }}>
              {editing ? 'ویرایش دسته‌بندی' : 'دسته‌بندی جدید'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560 }}>
              نام، مسیر، رسانه و سئو را مرحله‌به‌مرحله تکمیل کنید.
            </Typography>
          </DialogTitle>

          <Box sx={{ px: 2, flexShrink: 0, borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Tabs
              value={formTab}
              onChange={(_, v) => setFormTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.9rem', minHeight: 48 },
                '& .Mui-selected': { color: '#1C3B9B' },
                '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: '#1C3B9B' },
              }}
            >
              <Tab label="اطلاعات اصلی" />
              <Tab label="رسانه و ظاهر" />
              <Tab label="سئو و اشتراک‌گذاری" />
            </Tabs>
          </Box>

          <DialogContent
            dividers
            sx={{
              pt: 2.5,
              bgcolor: '#fff',
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box sx={{ display: formTab === 0 ? 'block' : 'none' }} role="tabpanel" aria-hidden={formTab !== 0}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="نام نمایشی"
                    fullWidth
                    required
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    helperText="عنوانی که در سایت دیده می‌شود"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Slug (مسیر URL)"
                    fullWidth
                    required
                    value={form.slug}
                    onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                    sx={{ '& input': { fontFamily: 'ui-monospace, monospace' } }}
                    helperText="فقط حروف انگلیسی، اعداد و خط تیره؛ یکتا در کل سیستم"
                  />
                </Grid>
                <Grid size={12}>
                  <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    startIcon={<AutoFixHighRoundedIcon />}
                    onClick={() => {
                      const s = suggestSlugFromName(form.name);
                      if (s) setForm((p) => ({ ...p, slug: s }));
                    }}
                    sx={{ textTransform: 'none', mb: 1 }}
                  >
                    پیشنهاد slug از نام
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>نوع محتوا</InputLabel>
                    <Select
                      label="نوع محتوا"
                      value={form.type}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, type: e.target.value as CategoryType, parentId: '' }))
                      }
                    >
                      <MenuItem value="COURSE">دوره آموزشی</MenuItem>
                      <MenuItem value="PODCAST">پادکست</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>دسته والد</InputLabel>
                    <Select
                      label="دسته والد"
                      value={form.parentId}
                      onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value }))}
                    >
                      <MenuItem value="">بدون والد (ریشه)</MenuItem>
                      {parentOptions.map((parent) => (
                        <MenuItem key={parent.id} value={parent.id}>
                          {parent.path || parent.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="خلاصه کوتاه (زیرعنوان)"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.shortDescription}
                    onChange={(e) => setForm((p) => ({ ...p, shortDescription: e.target.value }))}
                    helperText="۱–۲ خط برای کارت‌ها و لیست‌ها؛ متفاوت از توضیحات کامل"
                  />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: '#0F172A' }}>
                    توضیحات کامل
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    ویرایشگر پیشرفته، راست‌چین و آپلود تصویر از همین فرم. در سایت با کلاس typographic Tailwind (prose)
                    رندر می‌شود: تیترهای H2/H3 تیره، پاراگراف با فاصله مناسب، لیست‌ها، تصویر گرد با حداکثر عرض، لینک آبی برند (#3A68C2).
                    {form.type === 'COURSE'
                      ? ' اگر گزینهٔ «معرف کتابخانه» را فعل کنید، همین متن داخل بنر صفحهٔ /courses (بدون انتخاب دسته) هم دیده می‌شود.'
                      : ''}
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'rgba(15, 23, 42, 0.1)',
                      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                      '& .tinymce-rich-editor .tox-tinymce': {
                        borderRadius: '8px',
                        border: 'none',
                      },
                    }}
                  >
                    <RichTextEditor
                      key={editing?.id ?? 'new-category'}
                      value={form.description}
                      onChange={(html) => setForm((p) => ({ ...p, description: html }))}
                      height={360}
                      placeholder="معرفی حرفه‌ای دسته، مزایا، مخاطب هدف، ساختار دوره‌ها..."
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="ترتیب نمایش"
                    fullWidth
                    type="number"
                    slotProps={{ htmlInput: { min: 0, step: 1 } }}
                    value={form.sortOrder}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setForm((p) => ({ ...p, sortOrder: 0 }));
                        return;
                      }
                      const n = Number(raw);
                      setForm((p) => ({
                        ...p,
                        sortOrder: Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0,
                      }));
                    }}
                    helperText="۰ یا بیشتر؛ عدد کوچک‌تر معمولاً بالاتر نمایش داده می‌شود"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                  <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', gap: 2, pt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isActive}
                          onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label="فعال در سایت"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.isFeatured}
                          onChange={(e) => setForm((p) => ({ ...p, isFeatured: e.target.checked }))}
                          color="secondary"
                        />
                      }
                      label="دسته ویژه / برجسته"
                    />
                    {form.type === 'COURSE' ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.browseLandingIntro}
                            onChange={(e) =>
                              setForm((p) => ({
                                ...p,
                                browseLandingIntro: e.target.checked,
                              }))
                            }
                            color="primary"
                          />
                        }
                        label="معرف کتابخانهٔ دوره‌ها (کاوش)"
                      />
                    ) : null}
                  </Stack>
                </Grid>
                {form.type === 'COURSE' && form.browseLandingIntro ? (
                  <Grid size={12}>
                    <Typography variant="caption" color="primary.main" sx={{ display: 'block', fontWeight: 600 }}>
                      این دسته در فیلتر و مگامنو دیده نمی‌شود. «توضیحات کامل» همین فرم در بنر /courses نشان داده می‌شود وقتی موضوعی
                      انتخاب نشده. بهتر است فقط روی یک دسته فعال باشد؛ اگر چند تا فعال شود اولی (ترتیب کوچک‌تر sortOrder) استفاده می‌شود.
                    </Typography>
                  </Grid>
                ) : null}
              </Grid>
            </Box>

            <Box sx={{ display: formTab === 1 ? 'block' : 'none' }} role="tabpanel" aria-hidden={formTab !== 1}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageRoundedIcon fontSize="small" color="primary" />
                    تصویر شاخص
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5, lineHeight: 1.65, maxWidth: 720 }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                      راهنمای ابعاد (فرانت):{' '}
                    </Box>
                    {CATEGORY_COVER_HINT_FA}
                  </Typography>
                  <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onCoverFile} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: '100%',
                        maxWidth: 220,
                        aspectRatio: '1 / 1',
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: 'action.hover',
                        border: '1px dashed',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundImage: coverPreviewUrl ? `url(${coverPreviewUrl})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      {!coverPreviewUrl && (
                        <Typography variant="caption" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                          پیش‌نمایش مربع ۱:۱ — مثل بندانگشتی کنار عنوان در /courses؛ JPG، PNG یا WebP تا ۵۰MB
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={1.5} sx={{ flex: 1, minWidth: 200 }}>
                      <Button
                        type="button"
                        variant="contained"
                        startIcon={<CloudUploadRoundedIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={imageUploading}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          background: 'linear-gradient(to right, #1C3B9B, #C11277)',
                        }}
                      >
                        {imageUploading ? 'در حال آپلود...' : 'آپلود تصویر'}
                      </Button>
                      {imageUploading && <LinearProgress />}
                      <TextField
                        label="یا آدرس فایل / کلید ذخیره‌شده"
                        fullWidth
                        size="small"
                        value={form.image}
                        onChange={(e) => setForm((p) => ({ ...p, image: e.target.value }))}
                        placeholder="مثال: images/file-123.jpg"
                      />
                      <Button
                        type="button"
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineRoundedIcon />}
                        onClick={() => setForm((p) => ({ ...p, image: '' }))}
                        disabled={!form.image}
                        sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                      >
                        حذف تصویر شاخص
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="آیکون (اختیاری)"
                    fullWidth
                    value={form.icon}
                    onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
                    placeholder="نام آیکن، کلاس یا ایموجی"
                    helperText="برای نمایش کنار نام در UI (در صورت پشتیبانی قالب)"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <TextField
                      label="رنگ تم (hex)"
                      fullWidth
                      value={form.color}
                      onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                      placeholder="#1C3B9B"
                      sx={{ flex: 1 }}
                    />
                    <input
                      type="color"
                      aria-label="انتخاب رنگ"
                      value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(form.color || '') ? form.color : '#1c3b9b'}
                      onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        padding: 0,
                        cursor: 'pointer',
                        marginTop: 8,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: formTab === 2 ? 'block' : 'none' }} role="tabpanel" aria-hidden={formTab !== 2}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => setForm((p) => ({ ...p, metaTitle: p.name }))}
                      sx={{ textTransform: 'none' }}
                    >
                      کپی عنوان از نام دسته
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.syncCoverToSocial}
                          onChange={(e) => setForm((p) => ({ ...p, syncCoverToSocial: e.target.checked }))}
                        />
                      }
                      label="تصویر OG و توییتر همان شاخص باشد"
                    />
                  </Stack>
                  <FormHelperText sx={{ mx: 0 }}>
                    {form.syncCoverToSocial
                      ? 'با ذخیره، فیلدهای ogImage و twitterImage از تصویر شاخص پر می‌شوند.'
                      : 'می‌توانید برای شبکه‌های اجتماعی تصویر جداگانه تعیین کنید.'}
                  </FormHelperText>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Meta Title"
                    fullWidth
                    value={form.metaTitle}
                    onChange={(e) => setForm((p) => ({ ...p, metaTitle: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Meta Keywords (با ویرگول)"
                    fullWidth
                    value={form.metaKeywordsText}
                    onChange={(e) => setForm((p) => ({ ...p, metaKeywordsText: e.target.value }))}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Meta Description"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.metaDescription}
                    onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Canonical URL"
                    fullWidth
                    value={form.canonicalUrl}
                    onChange={(e) => setForm((p) => ({ ...p, canonicalUrl: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Robots"
                    fullWidth
                    value={form.robots}
                    onChange={(e) => setForm((p) => ({ ...p, robots: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="OG Title"
                    fullWidth
                    value={form.ogTitle}
                    onChange={(e) => setForm((p) => ({ ...p, ogTitle: e.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="OG Description"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.ogDescription}
                    onChange={(e) => setForm((p) => ({ ...p, ogDescription: e.target.value }))}
                  />
                </Grid>
                {!form.syncCoverToSocial && (
                  <Grid size={12}>
                    <TextField
                      label="OG Image (مسیر یا URL)"
                      fullWidth
                      value={form.ogImage}
                      onChange={(e) => setForm((p) => ({ ...p, ogImage: e.target.value }))}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Twitter card</InputLabel>
                    <Select
                      label="Twitter card"
                      value={form.twitterCard}
                      onChange={(e) => setForm((p) => ({ ...p, twitterCard: e.target.value }))}
                    >
                      <MenuItem value="summary">summary</MenuItem>
                      <MenuItem value="summary_large_image">summary_large_image</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Twitter Title"
                    fullWidth
                    value={form.twitterTitle}
                    onChange={(e) => setForm((p) => ({ ...p, twitterTitle: e.target.value }))}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Twitter Description"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.twitterDescription}
                    onChange={(e) => setForm((p) => ({ ...p, twitterDescription: e.target.value }))}
                  />
                </Grid>
                {!form.syncCoverToSocial && (
                  <Grid size={12}>
                    <TextField
                      label="Twitter Image"
                      fullWidth
                      value={form.twitterImage}
                      onChange={(e) => setForm((p) => ({ ...p, twitterImage: e.target.value }))}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Schema Type"
                    fullWidth
                    value={form.schemaType}
                    onChange={(e) => setForm((p) => ({ ...p, schemaType: e.target.value }))}
                  />
                </Grid>

                <Grid size={12}>
                  <Card sx={{ mt: 1, borderRadius: 2, bgcolor: '#f8fafc' }} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
                        <SellRoundedIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          پیش‌نمایش سئو و اشتراک
                        </Typography>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {ogPreviewUrl && (
                          <Box
                            component="img"
                            src={ogPreviewUrl}
                            alt=""
                            sx={{
                              width: 120,
                              height: 63,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body1" color="primary.main" sx={{ fontWeight: 600 }}>
                            {seoTitlePreview}
                          </Typography>
                          <Typography variant="caption" color="success.main" sx={{ wordBreak: 'break-all' }}>
                            {form.canonicalUrl || `/${form.slug || 'slug'}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {seoDescriptionPreview}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              gap: 1.5,
              flexShrink: 0,
              bgcolor: '#f8fafc',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button type="button" onClick={resetAndClose} disabled={imageUploading} sx={{ textTransform: 'none', fontWeight: 600 }}>
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving || imageUploading}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 3,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #1C3B9B 0%, #C11277 100%)',
                boxShadow: '0 8px 20px rgba(28, 59, 155, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #183387 0%, #a50f66 100%)',
                },
              }}
            >
              {isSaving ? 'در حال ذخیره...' : editing ? 'ذخیره تغییرات' : 'ایجاد دسته'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(moveTarget)}
        onClose={() => setMoveTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>جابه‌جایی در سلسله‌مراتب</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            والد جدید برای دسته «{moveTarget?.name}» را انتخاب کنید.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>والد جدید</InputLabel>
            <Select
              label="والد جدید"
              value={moveParentId}
              onChange={(e) => setMoveParentId(e.target.value)}
            >
              <MenuItem value="">بدون والد (Root)</MenuItem>
              {moveOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.path || option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveTarget(null)}>انصراف</Button>
          <Button onClick={handleMove} variant="contained">
            اعمال جابه‌جایی
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryFormDialogs;
